// Portrait purchase order summary: the purchase orders placed on one RO.
// Visual language matches the daily parts summary. pdf-lib cannot read theme tokens.
import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_TOP = 28;

const COLOR_BRAND = rgb(0x00 / 255, 0x71 / 255, 0xe3 / 255);
const COLOR_TEXT = rgb(0x2e / 255, 0x33 / 255, 0x38 / 255);
const COLOR_MUTED = rgb(0x8a / 255, 0x8a / 255, 0x8f / 255);
const COLOR_CARD = rgb(0xf5 / 255, 0xf5 / 255, 0xf7 / 255);
const COLOR_DIVIDER = rgb(0xe8 / 255, 0xe8 / 255, 0xea / 255);
const COLOR_CANCELLED = rgb(0x9b / 255, 0x2c / 255, 0x2c / 255);

export interface SupplierOrderSummaryPart {
    name: string;
    partNumber: string;
    /** RO number when the supplier replaced it with `partNumber`. */
    originalPartNumber?: string | null;
    supplierPartNumber: string | null;
    partType: string;
    qty: number;
    listPrice: number;
    /** Quoted list before an insurer or price-match adjustment. */
    quotedListPrice?: number | null;
    /** List after that adjustment, when it differs from the quoted list. */
    adjustedListPrice?: number | null;
    buyPrice: number;
    delivery: string;
    disposition: string | null;
}

export interface SupplierOrderSummaryOrder {
    supplierName: string;
    reference: string | null;
    status: string;
    source: string;
    placedAt: Date | null;
    parts: SupplierOrderSummaryPart[];
}

export interface SupplierOrderSummaryInput {
    generatedAt: Date;
    preparedBy?: string | null;
    roNumber: string;
    claimNumber?: string | null;
    insuranceName?: string | null;
    vin?: string | null;
    vehicle?: string | null;
    locationName?: string | null;
    orders: SupplierOrderSummaryOrder[];
}

interface PdfContext {
    pdf: PDFDocument;
    font: PDFFont;
    bold: PDFFont;
    page: PDFPage;
    y: number;
    input: SupplierOrderSummaryInput;
}

function pdfSafe(value: string | null | undefined): string {
    if (!value) return '';
    return value
        .replace(/\u00a0/g, ' ')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201c\u201d]/g, '"')
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/\u2026/g, '...')
        .replace(/\u2022/g, '*')
        .replace(/[^\x20-\x7E\u00A1-\u00FF]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

function fit(text: string, font: PDFFont, size: number, maxWidth: number): string {
    const clean = pdfSafe(text);
    if (!clean) return '-';
    if (font.widthOfTextAtSize(clean, size) <= maxWidth) return clean;
    let out = clean;
    while (out.length > 1 && font.widthOfTextAtSize(`${out}...`, size) > maxWidth) {
        out = out.slice(0, -1);
    }
    return `${out}...`;
}

function money(value: number): string {
    const abs = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${value < 0 ? '-' : ''}$${abs}`;
}

function formatStamp(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let hours = date.getHours();
    const ap = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${hours}:${minutes} ${ap}`;
}

function drawHeader(ctx: PdfContext, logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null, continued: boolean) {
    const top = PAGE_HEIGHT - MARGIN;
    const logoSize = 32;
    if (logo) {
        ctx.page.drawImage(logo, { x: MARGIN, y: top - logoSize, width: logoSize, height: logoSize });
    }
    const title = 'Purchase Order Summary';
    const titleSize = 18;
    const titleWidth = ctx.bold.widthOfTextAtSize(title, titleSize);
    ctx.page.drawText(title, {
        x: PAGE_WIDTH - MARGIN - titleWidth,
        y: top - 16,
        size: titleSize,
        font: ctx.bold,
        color: COLOR_BRAND,
    });
    const subtitle = continued
        ? `RO ${pdfSafe(ctx.input.roNumber) || '-'}  continued`
        : `RO ${pdfSafe(ctx.input.roNumber) || '-'}`;
    const subtitleWidth = ctx.font.widthOfTextAtSize(subtitle, 10);
    ctx.page.drawText(subtitle, {
        x: PAGE_WIDTH - MARGIN - subtitleWidth,
        y: top - 32,
        size: 10,
        font: ctx.font,
        color: COLOR_MUTED,
    });
    const dividerY = top - logoSize - 12;
    ctx.page.drawLine({
        start: { x: MARGIN, y: dividerY },
        end: { x: PAGE_WIDTH - MARGIN, y: dividerY },
        thickness: 1,
        color: COLOR_DIVIDER,
    });
    ctx.y = dividerY - 18;
}

function newPage(ctx: PdfContext, logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null) {
    ctx.page = ctx.pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    drawHeader(ctx, logo, true);
}

function ensure(ctx: PdfContext, needed: number, logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null) {
    if (ctx.y - needed < FOOTER_TOP + 8) newPage(ctx, logo);
}

function drawMeta(ctx: PdfContext, logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null) {
    const input = ctx.input;
    const pairs = [
        ['Shop', pdfSafe(input.locationName)],
        ['Vehicle', pdfSafe(input.vehicle)],
        ['Insurance', pdfSafe(input.insuranceName)],
        ['VIN', pdfSafe(input.vin)],
        ['Claim', pdfSafe(input.claimNumber)],
        ['Prepared by', pdfSafe(input.preparedBy)],
    ].filter((pair): pair is [string, string] => !!pair[1]);
    if (pairs.length === 0) return;
    const rowH = 14;
    const rows = Math.ceil(pairs.length / 2);
    const cardH = 12 + rows * rowH;
    ensure(ctx, cardH + 8, logo);
    const top = ctx.y;
    ctx.page.drawRectangle({
        x: MARGIN,
        y: top - cardH,
        width: CONTENT_WIDTH,
        height: cardH,
        color: COLOR_CARD,
    });
    const colW = CONTENT_WIDTH / 2;
    pairs.forEach((pair, index) => {
        const column = index % 2;
        const row = Math.floor(index / 2);
        const x = MARGIN + 10 + column * colW;
        const baseline = top - 16 - row * rowH;
        const label = `${pair[0]}  `;
        ctx.page.drawText(label, { x, y: baseline, size: 8, font: ctx.font, color: COLOR_MUTED });
        const labelW = ctx.font.widthOfTextAtSize(label, 8);
        ctx.page.drawText(fit(pair[1], ctx.bold, 9, colW - labelW - 16), {
            x: x + labelW,
            y: baseline,
            size: 9,
            font: ctx.bold,
            color: COLOR_TEXT,
        });
    });
    ctx.y = top - cardH - 16;
}

function partLines(part: SupplierOrderSummaryPart): string[] {
    const idBits = [
        part.originalPartNumber
            ? `OE ${pdfSafe(part.originalPartNumber)}   Ordered ${pdfSafe(part.partNumber)}`
            : (part.partNumber ? `OE ${pdfSafe(part.partNumber)}` : ''),
        part.supplierPartNumber ? `Supplier ${pdfSafe(part.supplierPartNumber)}` : '',
        pdfSafe(part.partType),
        `Qty ${part.qty}`,
        pdfSafe(part.delivery),
    ].filter(Boolean);
    const quoted = part.quotedListPrice;
    const adjusted = part.adjustedListPrice;
    const hasAdjustment = quoted != null && adjusted != null && adjusted !== quoted;
    const disposition = pdfSafe(part.disposition);
    const buy = `Buy ${money(part.buyPrice)}`;
    const lines = [idBits.join('   ·   ')];
    if (hasAdjustment) {
        lines.push(`Quoted list ${money(quoted)}    Adjusted list ${money(adjusted)}`);
        const orderedDiffers = Math.abs(part.listPrice - adjusted) > 0.009;
        const price = orderedDiffers ? `List ${money(part.listPrice)}    ${buy}` : buy;
        lines.push(disposition ? `${price}    ${disposition}` : price);
    } else {
        const price = `List ${money(part.listPrice)}    ${buy}`;
        lines.push(disposition ? `${price}    ${disposition}` : price);
    }
    return lines;
}

function effectiveDisposition(order: SupplierOrderSummaryOrder, part: SupplierOrderSummaryPart): string | null {
    if (part.disposition) return part.disposition;
    const status = order.status.toLowerCase();
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'rejected') return 'Rejected';
    return null;
}

function omissionPhrase(parts: SupplierOrderSummaryPart[]): string {
    const omitted = parts.filter((part) => part.disposition);
    if (omitted.length === 0) return '';
    const known = ['cancelled', 'returned', 'rejected'];
    const present = known.filter((reason) => omitted.some((part) => (part.disposition || '').toLowerCase() === reason));
    const reasons = present.length > 0 ? present : ['excluded'];
    const text = reasons.length === 1
        ? reasons[0]
        : reasons.length === 2
            ? `${reasons[0]} or ${reasons[1]}`
            : `${reasons.slice(0, -1).join(', ')}, or ${reasons[reasons.length - 1]}`;
    return `    ${omitted.length} ${text}, not in totals`;
}

function drawSupplierOrder(
    ctx: PdfContext,
    logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null,
    order: SupplierOrderSummaryOrder,
) {
    const parts = order.parts.map((part) => ({ ...part, disposition: effectiveDisposition(order, part) }));
    const activeParts = parts.filter((part) => !part.disposition);
    const listTotal = activeParts.reduce((sum, part) => sum + part.listPrice, 0);
    const buyTotal = activeParts.reduce((sum, part) => sum + part.buyPrice, 0);
    const omittedLabel = omissionPhrase(parts);
    const heading = [
        pdfSafe(order.status),
        order.reference ? `Order ${pdfSafe(order.reference)}` : '',
        pdfSafe(order.source),
        order.placedAt ? formatStamp(order.placedAt) : '',
    ].filter(Boolean).join('   ·   ');

    const drawTitle = () => {
        ensure(ctx, 46, logo);
        ctx.page.drawText(fit(order.supplierName, ctx.bold, 13, CONTENT_WIDTH), {
            x: MARGIN,
            y: ctx.y - 14,
            size: 13,
            font: ctx.bold,
            color: COLOR_BRAND,
        });
        ctx.page.drawText(fit(heading || '-', ctx.font, 9, CONTENT_WIDTH), {
            x: MARGIN,
            y: ctx.y - 28,
            size: 9,
            font: ctx.font,
            color: COLOR_MUTED,
        });
        ctx.y -= 36;
    };

    drawTitle();
    if (parts.length === 0) {
        ensure(ctx, 16, logo);
        ctx.page.drawText('No parts on this purchase order.', {
            x: MARGIN,
            y: ctx.y - 10,
            size: 9,
            font: ctx.font,
            color: COLOR_MUTED,
        });
        ctx.y -= 20;
        return;
    }

    for (const part of parts) {
        const lines = partLines(part);
        const blockH = 16 + lines.length * 12;
        if (ctx.y - blockH < FOOTER_TOP + 8) {
            newPage(ctx, logo);
            drawTitle();
        }
        const cancelled = !!part.disposition;
        ctx.page.drawText(fit(part.name, ctx.bold, 10, CONTENT_WIDTH), {
            x: MARGIN,
            y: ctx.y - 11,
            size: 10,
            font: ctx.bold,
            color: cancelled ? COLOR_CANCELLED : COLOR_TEXT,
        });
        lines.forEach((line, index) => {
            ctx.page.drawText(fit(line, ctx.font, 8, CONTENT_WIDTH), {
                x: MARGIN,
                y: ctx.y - 24 - index * 12,
                size: 8,
                font: ctx.font,
                color: cancelled ? COLOR_CANCELLED : COLOR_MUTED,
            });
        });
        ctx.y -= blockH + 6;
    }

    ensure(ctx, 22, logo);
    const activeLabel = `${activeParts.length} active part${activeParts.length === 1 ? '' : 's'}`;
    const totals = `${activeLabel}    List ${money(listTotal)}    Buy ${money(buyTotal)}${omittedLabel}`;
    ctx.page.drawText(totals, { x: MARGIN, y: ctx.y - 10, size: 9, font: ctx.bold, color: COLOR_TEXT });
    ctx.y -= 16;
    ctx.page.drawLine({
        start: { x: MARGIN, y: ctx.y },
        end: { x: PAGE_WIDTH - MARGIN, y: ctx.y },
        thickness: 0.6,
        color: COLOR_DIVIDER,
    });
    ctx.y -= 16;
}

function drawFooters(ctx: PdfContext) {
    const pages = ctx.pdf.getPages();
    pages.forEach((page, index) => {
        const note = 'Yaxle purchase order summary for this RO.';
        page.drawText(note, { x: MARGIN, y: 16, size: 7, font: ctx.font, color: COLOR_MUTED });
        const label = `Page ${index + 1} of ${pages.length}`;
        const width = ctx.font.widthOfTextAtSize(label, 7);
        page.drawText(label, { x: PAGE_WIDTH - MARGIN - width, y: 16, size: 7, font: ctx.font, color: COLOR_MUTED });
    });
}

export async function generateSupplierOrderSummaryPdf(
    input: SupplierOrderSummaryInput,
    logoPng?: Uint8Array | null,
): Promise<Uint8Array> {
    const pdf = await PDFDocument.create();
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
    let logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null = null;
    if (logoPng && logoPng.byteLength > 0) {
        try {
            logo = await pdf.embedPng(logoPng);
        } catch {
            logo = null;
        }
    }
    const ctx: PdfContext = {
        pdf,
        font,
        bold,
        page: pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]),
        y: 0,
        input,
    };
    drawHeader(ctx, logo, false);
    drawMeta(ctx, logo);
    if (input.orders.length === 0) {
        ctx.page.drawText('This RO has no purchase orders.', {
            x: MARGIN,
            y: ctx.y - 12,
            size: 11,
            font: ctx.font,
            color: COLOR_MUTED,
        });
    } else {
        for (const order of input.orders) drawSupplierOrder(ctx, logo, order);
    }
    drawFooters(ctx);
    return pdf.save();
}
